import type { Category } from "@/types/product"

export type CatalogRootGroups = {
  mainRoots: Category[]
  miscRoots: Category[]
}

export type CatalogDisplayNode = {
  category: Category
  href: string
  children: CatalogDisplayNode[]
}

export const hasCategoryChildren = (category: Category) =>
  Array.isArray(category.children) && category.children.length > 0

export const getCategoryHref = (category: Category) =>
  `/category/${category.ref_key}`

export const splitRootCategories = (categories: Category[]): CatalogRootGroups =>
  categories.reduce<CatalogRootGroups>(
    (groups, category) => {
      if (hasCategoryChildren(category)) {
        groups.mainRoots.push(category)
      } else {
        groups.miscRoots.push(category)
      }

      return groups
    },
    { mainRoots: [], miscRoots: [] }
  )

export const pickInitialCategory = (categories: Category[]) =>
  categories.find(hasCategoryChildren) ?? categories[0] ?? null

export const normalizeCategoryTree = (category: Category): CatalogDisplayNode => ({
  category,
  href: getCategoryHref(category),
  children: (category.children ?? []).map(normalizeCategoryTree),
})

export const getCatalogDisplayWeight = (node: CatalogDisplayNode): number =>
  1 + node.children.reduce((weight, child) => weight + getCatalogDisplayWeight(child), 0)

export const splitCatalogDisplayColumns = (
  nodes: CatalogDisplayNode[]
): [CatalogDisplayNode[], CatalogDisplayNode[]] => {
  const columns: [CatalogDisplayNode[], CatalogDisplayNode[]] = [[], []]
  const weights = [0, 0]

  nodes.forEach((node) => {
    const targetColumn = weights[0] <= weights[1] ? 0 : 1

    columns[targetColumn].push(node)
    weights[targetColumn] += getCatalogDisplayWeight(node)
  })

  return columns
}

export const flattenCategories = (categories: Category[]): Category[] =>
  categories.flatMap((category) => [
    category,
    ...flattenCategories(category.children ?? []),
  ])

export const hasMeaningfulDescription = (category: Category) => {
  const description = category.description.trim()
  return (
    description.length > 0 &&
    description.toLocaleLowerCase() !== category.name.trim().toLocaleLowerCase()
  )
}
